import moment from 'moment'
import { useReselector, useToggle } from '@/utils/sharedHooks'

import {
  Transaction,
  TransactionOrSplit,
} from '@/reducers/admin/allTransactions.slice'
import { Card, CardColors, Text, Tooltip } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { EditableTransactionTypeLabel } from '@/components/EditableTransactionTypeLabel'
import { DisplayRowIcon } from './TransactionTableRow'
import { TransactionCategoryLabel } from '@/components/TransactionCategoryLabel'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { getLockedTransactionDescription } from '../copyConstants'
import {
  selectIsLockedTransaction,
  selectIsSplitTransaction,
} from '../transactions.selectors'

import { UploadDocumentType } from '@/constants/businessConstants'
import TransactionDetailsMenu from './TransactionDetailsMenu'
import { selectIsUserManualAccount } from '@/selectors/financeSelectors'

// TODO: Migrate to v2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'

const TypeField = ({
  transaction,
  review,
}: {
  transaction: TransactionOrSplit
  review?: boolean
}) => {
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )

  if (isSplitTransaction) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Split Transaction
      </Text>
    )
  }
  return (
    <EditableTransactionTypeLabel transaction={transaction} review={review} />
  )
}

const AmountField = ({ transaction }: { transaction: TransactionOrSplit }) => {
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )
  return (
    <>
      {isSplitTransaction && (
        <s>
          <Text variant={TypographyStyles.BODY_SM}>
            <CurrencyFormatLabel
              value={centsToDollars(transaction.amountInCents)}
            />
          </Text>
        </s>
      )}
      {!isSplitTransaction && (
        <Text variant={TypographyStyles.BODY_SM}>
          <CurrencyFormatLabel
            value={centsToDollars(transaction.amountInCents)}
          />
        </Text>
      )}
    </>
  )
}

const LastRow = ({
  transaction,
  review,
  reports,
}: {
  transaction: TransactionOrSplit
  review?: boolean
  reports?: boolean
}) => {
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom || transaction.id
  )

  const isManualTransaction = useReselector(
    selectIsUserManualAccount,
    transaction.financialAccountId
  )

  const [fileModalOpen, toggleFileModal] = useToggle(false)

  return (
    <>
      <div className="flex flex-col sm:flex-row items-center justify-between w-full">
        <div className="flex items-center sm:w-2/12">
          <DisplayRowIcon
            isLocked={locked}
            isAutoCategorized={Boolean(transaction.autocategorizedAt)}
            isManualTransaction={isManualTransaction}
          />
        </div>

        <div className="flex justify-end items-center gap-[20.5px] sm:w-10/12 w-full mt-2 sm:mt-0">
          <TransactionCategoryLabel
            editable={!reports}
            variant="text"
            transaction={transaction}
            review={review}
            filtered
            useRecategorizationModal
          />

          {!reports && !locked && (
            <TransactionDetailsMenu
              transaction={transaction}
              review={review}
              toggleFileModal={toggleFileModal}
              isManualTransaction={isManualTransaction}
            />
          )}
        </div>
      </div>
      <FileUploadModal
        open={fileModalOpen}
        close={toggleFileModal}
        documentType={UploadDocumentType.RECEIPT}
        userFacing
        extraPayload={{
          transactionId: Number(transaction.splitFrom || transaction.id),
        }}
      />
    </>
  )
}

const SplitTransactionRow = ({
  transaction,
  review,
  reports,
}: {
  transaction: TransactionOrSplit
  review?: boolean
  reports?: boolean
}) => {
  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="w-full space-y-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full">
          {!reports && (
            <div className="sm:w-1/2 w-full">
              <TypeField transaction={transaction} review={review} />
            </div>
          )}

          <div
            className={`${!reports ? 'sm:w-1/2' : 'w-full'} w-full text-right mt-2 sm:mt-0`}
          >
            <AmountField transaction={transaction} />
          </div>
        </div>

        <LastRow transaction={transaction} review={review} reports={reports} />
      </div>
    </Card>
  )
}

const RowContent = ({
  transaction,
  review,
  reports,
}: {
  transaction: Transaction
  review?: boolean
  reports?: boolean
}) => {
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )
  const locked = useReselector(selectIsLockedTransaction, transaction.id)
  return (
    <div
      className={`w-full flex flex-col border-b border-neutral-300 py-4 ${
        locked && !reports
          ? 'bg-muted opacity-60 pointer-events-none cursor-not-allowed'
          : ''
      }`}
      {...(locked && !reports ? { 'aria-disabled': 'true' } : {})}
    >
      <div className="flex flex-col sm:flex-row w-full pb-2">
        <div className="sm:w-2/12 w-full">
          <Text variant={TypographyStyles.BODY_SM}>
            <b>
              {moment.utc(transaction.date).format(DATE_FORMATS.DISPLAY_SHORT)}
            </b>
          </Text>
        </div>

        <div className="sm:w-2/12 w-full text-right mt-2 sm:mt-0">
          <AmountField transaction={transaction} />
        </div>

        <div className="sm:w-4/12 w-full text-left mt-2 sm:mt-0">
          <Text variant={TypographyStyles.BODY_SM}>
            <i>{transaction.nickname}</i>
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            {transaction.description}
          </Text>
        </div>

        {reports && (
          <div className="sm:w-4/12 w-full mt-2 sm:mt-0">
            <TransactionCategoryLabel
              editable={!reports}
              transaction={transaction}
              review={review}
              filtered
              useRecategorizationModal
            />
          </div>
        )}
      </div>

      {!reports && (
        <div className="pb-2">
          <TypeField transaction={transaction} review={review} />
        </div>
      )}

      {!isSplitTransaction && !reports && (
        <LastRow transaction={transaction} review={review} reports={reports} />
      )}

      {isSplitTransaction &&
        !reports &&
        transaction.splitTransactions?.map((splitTransaction) => (
          <div key={splitTransaction.id} className="split-transaction-row">
            <SplitTransactionRow
              transaction={splitTransaction}
              review={review}
              reports={reports}
            />
          </div>
        ))}
    </div>
  )
}

export const MobileTableRow = ({
  transaction,
  review,
  reports,
}: {
  transaction: Transaction
  review?: boolean
  reports?: boolean
}) => {
  const locked = useReselector(selectIsLockedTransaction, transaction.id)
  if (locked && !reports) {
    return (
      <div className="flex-grow w-full">
        <Tooltip>
          <Tooltip.Trigger>
            <RowContent transaction={transaction} review={review} />
          </Tooltip.Trigger>
          <Tooltip.Content>
            {getLockedTransactionDescription().transactionRowLockIconTooltip}
          </Tooltip.Content>
        </Tooltip>
      </div>
    )
  } else {
    return (
      <RowContent transaction={transaction} review={review} reports={reports} />
    )
  }
}
